import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { isCommentRichTextEmpty } from '../utils/comment-rich-text';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { RichTextField, type MentionableEntity } from './RichTextField';

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (content: string) => void;
  selectedText: string;
  isLoading?: boolean;
  mentionableEntities?: MentionableEntity[];
}

export const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  selectedText,
  isLoading = false,
  mentionableEntities = [],
}) => {
  const { t } = useLocale();
  const [content, setContent] = useState('');
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setContent('');
      setEditorKey((key) => key + 1);
    }
  }, [isOpen]);

  const canSubmit = !isLoading && !isCommentRichTextEmpty(content);

  const submitComment = (): void => {
    if (!canSubmit) {
      return;
    }
    onSubmit(content);
    setContent('');
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    submitComment();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="max-h-[80vh] overflow-auto sm:max-w-[500px]"
        aria-describedby={undefined}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('add_comment')}</DialogTitle>
        </DialogHeader>
        {selectedText && (
          <div className="rounded-md bg-muted px-3 py-2 text-sm italic text-muted-foreground">
            "{selectedText}"
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <RichTextField
            key={editorKey}
            value={content}
            onChange={setContent}
            placeholder={t('comment_placeholder')}
            mentionableEntities={mentionableEntities}
            minHeight="120px"
            maxHeight="40vh"
            editable={!isLoading}
            autoFocus
            onSubmit={submitComment}
            onCancel={onClose}
          />
          <DialogFooter>
            <CancelButton
              type="button"
              onClick={onClose}
              disabled={isLoading}
             />
            <Button
              type="submit"
              disabled={!canSubmit}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('sending')}
                </span>
              ) : (
                <>
                  {t('create_comment')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
